import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  History as HistoryIcon,
} from '@mui/icons-material';
import { fetchProductAuditLog, fetchVariantAuditLog } from '../api/auditLog';
import type { AuditLogItem, AuditOrder } from '../types/auditLog';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DATE_FMT = new Intl.DateTimeFormat('es-MX', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const TIME_FMT = new Intl.DateTimeFormat('es-MX', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatDayKey(iso: string): string {
  try {
    return new Date(iso).toDateString();
  } catch {
    return '?';
  }
}

function formatDayLabel(iso: string): string {
  try {
    const label = DATE_FMT.format(new Date(iso));
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return '?';
  }
}

function formatTime(iso: string): string {
  try {
    return TIME_FMT.format(new Date(iso));
  } catch {
    return '?';
  }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  const s = String(v).trim();
  return s === '' ? '(vacío)' : s;
}

// ─── Kind chip ────────────────────────────────────────────────────────────────

const KIND_LABELS: Record<string, string> = {
  product: 'Producto',
  productImage: 'Imagen de producto',
  variant: 'Variante',
  variantImage: 'Imagen de variante',
};

const KIND_COLORS: Record<string, { bg: string; color: string }> = {
  product: { bg: '#EFF6FF', color: '#1D4ED8' },
  productImage: { bg: '#F5F3FF', color: '#6D28D9' },
  variant: { bg: '#F0FDFA', color: '#0F766E' },
  variantImage: { bg: '#FFF7ED', color: '#C2410C' },
};

function KindChip({ kind }: { kind: string }): React.ReactElement {
  const style = KIND_COLORS[kind] ?? { bg: '#F1F5F9', color: '#475569' };
  return (
    <Chip
      label={KIND_LABELS[kind] ?? kind}
      size="small"
      sx={{
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 700,
        fontSize: 10,
        height: 20,
        letterSpacing: 0.2,
      }}
    />
  );
}

// ─── Operation chip ───────────────────────────────────────────────────────────

const OP_COLORS: Record<string, { bg: string; color: string }> = {
  INSERT: { bg: '#F0FDF4', color: '#15803D' },
  UPDATE: { bg: '#FEFCE8', color: '#A16207' },
  DELETE: { bg: '#FEF2F2', color: '#B91C1C' },
  BULK_UPDATE: { bg: '#EFF6FF', color: '#1D4ED8' },
};

const OP_LABELS: Record<string, string> = {
  INSERT: 'Creado',
  UPDATE: 'Actualizado',
  DELETE: 'Eliminado',
  BULK_UPDATE: 'Actualización masiva',
};

function OperationChip({ operation }: { operation: string }): React.ReactElement {
  const style = OP_COLORS[operation] ?? { bg: '#F1F5F9', color: '#475569' };
  return (
    <Chip
      label={OP_LABELS[operation] ?? operation}
      size="small"
      sx={{
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 700,
        fontSize: 10,
        height: 20,
        letterSpacing: 0.2,
      }}
    />
  );
}

// ─── Change diff table ────────────────────────────────────────────────────────

function ChangesTable({ item }: { item: AuditLogItem }): React.ReactElement | null {
  if (item.changes.length === 0) {
    if (item.operation === 'INSERT') {
      return (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
          Registro creado — activa &laquo;incluir snapshots&raquo; en el endpoint para ver los
          valores iniciales.
        </Typography>
      );
    }
    if (item.operation === 'DELETE') {
      return (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
          Registro eliminado.
        </Typography>
      );
    }
    return null;
  }

  return (
    <Table
      size="small"
      sx={{ mt: 1, '& td': { py: 0.5, fontSize: 12, verticalAlign: 'top', border: 0 } }}
    >
      <TableBody>
        {item.changes.map((c, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`${c.field}-${i}`}>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 600, minWidth: 120, pr: 1.5 }}>
              {c.field}
            </TableCell>
            <TableCell sx={{ color: '#B91C1C', maxWidth: 180, wordBreak: 'break-all' }}>
              {formatValue(c.before)}
            </TableCell>
            <TableCell sx={{ color: 'text.disabled', px: 0.5 }}>→</TableCell>
            <TableCell sx={{ color: '#15803D', maxWidth: 180, wordBreak: 'break-all' }}>
              {formatValue(c.after)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── Single event card ────────────────────────────────────────────────────────

function EventCard({ item }: { item: AuditLogItem }): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const hasChanges =
    item.changes.length > 0 || item.operation === 'INSERT' || item.operation === 'DELETE';

  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      {/* Timeline spine */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: '#111827',
            mt: 0.6,
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          pb: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minWidth: 0,
        }}
      >
        {/* Time + chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
          >
            {formatTime(item.at)}
          </Typography>
          <KindChip kind={item.kind} />
          <OperationChip operation={item.operation} />
          {item.imageId != null && (
            <Typography variant="caption" color="text.disabled">
              img #{item.imageId}
            </Typography>
          )}
        </Box>

        {/* Label */}
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, lineHeight: 1.4, color: 'text.primary' }}
        >
          {item.label || `${item.operation} — ${item.kind}`}
        </Typography>

        {/* Expand toggle */}
        {hasChanges && (
          <Box
            component="button"
            type="button"
            onClick={toggleExpanded}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.75,
              border: 'none',
              background: 'none',
              p: 0,
              cursor: 'pointer',
              color: 'text.secondary',
              font: 'inherit',
              fontSize: 11,
              fontWeight: 600,
              '&:hover': { color: 'text.primary' },
            }}
          >
            {expanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
            {item.changes.length > 0
              ? `${item.changes.length} campo${item.changes.length !== 1 ? 's' : ''} modificado${item.changes.length !== 1 ? 's' : ''}`
              : 'Ver detalles'}
          </Box>
        )}

        <Collapse in={expanded} unmountOnExit>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: '#F8FAFC',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ChangesTable item={item} />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

// ─── Day group ────────────────────────────────────────────────────────────────

interface DayGroup {
  key: string;
  label: string;
  items: AuditLogItem[];
}

function groupByDay(items: AuditLogItem[]): DayGroup[] {
  const map = items.reduce<Map<string, DayGroup>>((acc, item) => {
    const key = formatDayKey(item.at);
    if (!acc.has(key)) {
      acc.set(key, { key, label: formatDayLabel(item.at), items: [] });
    }
    acc.get(key)!.items.push(item);
    return acc;
  }, new Map());
  return Array.from(map.values());
}

// ─── Public props ─────────────────────────────────────────────────────────────

export interface AuditHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  /** 'product' uses productId; 'variant' uses variantId (= inventoryId). */
  mode: 'product' | 'variant';
  /** The ID to query — productId or variantId. */
  entityId: number | null;
  /** Displayed in the dialog header for context. */
  entityTitle?: string;
}

// ─── Main component ───────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export default function AuditHistoryDrawer({
  open,
  onClose,
  mode,
  entityId,
  entityTitle,
}: AuditHistoryDrawerProps): React.ReactElement {
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState<AuditOrder>('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (p: number, o: AuditOrder) => {
      if (!entityId) return;
      setLoading(true);
      setError(null);
      const query = { page: p, pageSize: PAGE_SIZE, order: o };
      const result =
        mode === 'product'
          ? await fetchProductAuditLog(entityId, query)
          : await fetchVariantAuditLog(entityId, query);
      setLoading(false);
      if (result.ok) {
        setItems(result.data.items);
        setTotal(result.data.total);
      } else {
        setError(result.error);
        setItems([]);
        setTotal(0);
      }
    },
    [entityId, mode]
  );

  useEffect(() => {
    if (open && entityId) {
      setPage(1);
      setOrder('desc');
      load(1, 'desc');
    } else {
      setItems([]);
      setTotal(0);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId]);

  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
      load(value, order);
    },
    [load, order]
  );

  const handleOrderChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: AuditOrder | null) => {
      if (!value) return;
      setOrder(value);
      setPage(1);
      load(1, value);
    },
    [load]
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const groups = groupByDay(items);

  const entityLabel = mode === 'product' ? 'producto' : 'variante';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#111827',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              <HistoryIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                Historial de cambios
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                {entityTitle ? (
                  <>
                    {entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1)}:{' '}
                    <Box component="span" fontWeight={700} color="text.primary">
                      {entityTitle}
                    </Box>
                  </>
                ) : (
                  `Historial del ${entityLabel} #${entityId}`
                )}
              </Typography>
              {total > 0 && !loading && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {total} evento{total !== 1 ? 's' : ''} en total
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Order toggle */}
            <ToggleButtonGroup
              value={order}
              exclusive
              onChange={handleOrderChange}
              size="small"
              sx={{ '& .MuiToggleButton-root': { py: 0.5, px: 1 } }}
            >
              <Tooltip title="Más reciente primero">
                <ToggleButton value="desc">
                  <ArrowDownward sx={{ fontSize: 16 }} />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Más antiguo primero">
                <ToggleButton value="asc">
                  <ArrowUpward sx={{ fontSize: 16 }} />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#111827' }} />
          </Box>
        )}

        {/* Error */}
        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Empty */}
        {!loading && !error && items.length === 0 && (
          <Box
            sx={{
              py: 12,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <HistoryIcon sx={{ fontSize: 56, color: 'grey.300' }} />
            <Typography variant="h6" color="text.secondary">
              Sin eventos registrados
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Aún no hay cambios en el historial de este {entityLabel}.
            </Typography>
          </Box>
        )}

        {/* Timeline */}
        {!loading && !error && items.length > 0 && (
          <Box sx={{ px: 3, pt: 3 }}>
            {groups.map((group, gi) => (
              <Box key={group.key}>
                {/* Day divider */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                    mt: gi === 0 ? 0 : 3,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {group.label}
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                {/* Events */}
                <Box
                  sx={{
                    ml: 0.5,
                    pl: 2,
                    borderLeft: '2px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {group.items.map((item) => (
                    <EventCard key={item.id} item={item} />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* Pagination footer */}
      {!loading && !error && totalPages > 1 && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.disabled">
            Página {page} de {totalPages} · {total} evento{total !== 1 ? 's' : ''}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            size="small"
            sx={{
              '& .MuiPaginationItem-root.Mui-selected': {
                bgcolor: '#111827',
                color: 'white',
              },
            }}
          />
        </Box>
      )}
    </Dialog>
  );
}

AuditHistoryDrawer.defaultProps = {
  entityTitle: undefined,
};
